import { API } from '@shared/api'
import parseCookie from '@shared/lib/helpers/parseCookie.js'

const GITLAB_STATUS = process.env.GITLAB_STATUS || null
const IS_PROD = GITLAB_STATUS === '0'

type callback = {
  (param?: any): void
}

type TypeListenersKey =
  | 'openAuth'
  | 'loginStart'
  | 'loginSuccess'
  | 'loginError'
  | 'logoutStart'
  | 'logoutSuccess'
  | 'logoutError'

type TypeListeners = {
  openAuth: callback[]
  loginStart: callback[]
  loginSuccess: callback[]
  loginError: callback[]
  logoutStart: callback[]
  logoutSuccess: callback[]
  logoutError: callback[]
}

declare global {
  interface Window {
    ramblerIdHelper: any
  }
}

// глобальные переменные для состояния
let isInit = false

/**
 * @constant listeners
 */
const listeners: TypeListeners = {
  openAuth: [],
  loginStart: [],
  loginSuccess: [],
  loginError: [],
  logoutStart: [],
  logoutSuccess: [],
  logoutError: [],
}

/**
 * @function _callListeners
 */
const _callListeners = (name: TypeListenersKey, data = null) => {
  listeners[name].forEach((callback) => callback(data))
}

/**
 * @function addListener
 */
const addListener = (name: TypeListenersKey, callback) => {
  const hasEvent = Object.keys(listeners).includes(name)
  const listenerArray = listeners[name]

  if (!hasEvent) {
    throw new Error(`События ${name} не существует в классе Authorization`)
  }

  if (listenerArray.length >= 10) {
    throw new Error(`Превышено количество коллбеков на событие ${name} в классе Authorization. Max = 10`)
  }

  // проверяем есть ли в массиве такой метод, для избежания дублирования
  const checkThisMethodInArray = listenerArray.findIndex((oldCallback) => oldCallback === callback)

  if (checkThisMethodInArray === -1) {
    listenerArray.push(callback)
  }
}

/**
 * @function removeListener
 */
const removeListener = (name: TypeListenersKey, callback) => {
  const hasEvent = Object.keys(listeners).includes(name)
  if (!hasEvent) throw new Error(`События ${name} не существует в классе Authorization`)

  const listenerArray = listeners[name]

  const findIndexRemoveCallback = listenerArray.findIndex((oldCallback) => oldCallback === callback)
  if (findIndexRemoveCallback === -1) return

  listenerArray.splice(findIndexRemoveCallback, 1)
}

/**
 * @function openAuth
 */
const openAuth = () => {
  authorizationRamblerIdOpenAuth()
  _callListeners('openAuth')
}

/**
 * @function isAuthorized
 * @description проверка авторизован ли пользователь в Rambler ID
 *    если авторизован то возвращает токен для authorizationRestsLogin
 */
const isAuthorized = () =>
  new Promise((resolve, reject) => {
    authorizationRamblerIdGetSessionToken(async (response) => {
      response?.token ? resolve(response.token) : reject(response)
    })
  })

/**
 * @function login
 * @description получить данные пользователя
 *    данные возвращаются только когда пользователь авторизован в Rambler ID
 */
const login = () => {
  _callListeners('loginStart')

  isAuthorized()
    .then(async (token) => {
      const resultLogin = await authorizationRestsLogin(token)
      const userData = resultLogin ? await API.user_me({ repeated: true }) : null
      if (!userData) throw new Error('Не удалось получить данные пользователя')

      authorizationRamblerIdCloseAuth()

      _callListeners('loginSuccess', userData)
      await authorizationAfishaLogin(token)
    })
    .catch((error) => {
      _callListeners('loginError', error)
    })
}

/**
 * @function logout
 */
const logout = async () => {
  _callListeners('logoutStart')

  const callback = async () => {
    const status = await authorizationRestsLogout()
    _callListeners(status ? 'logoutSuccess' : 'logoutError')
    authorizationRamblerIdRemoveLogout(callback)
  }

  // await authorizationAfishaLogout()
  authorizationRamblerIdOnLogout(callback)
  authorizationRamblerIdLogout()

  window.location.reload()
}

/**
 * @function init
 * @description подписка на события Rambler ID
 */
const init = () => {
  !IS_PROD &&
    Object.keys(listeners).forEach((listenerName) => {
      listeners[listenerName].push((data) => console.log(listenerName, data))
    })

  isInit = true

  // авторизация стандартным способом
  authorizationRamblerIdOnLogin(login)

  // авторизация через соцсети
  authorizationRamblerIdOnOauthLogin(login)
}

// * функции для работы с REST API *

/**
 * @function authorizationRestsLogin
 * @description
 * @param sessionToken
 * @returns true | false
 */
const authorizationRestsLogin = async (sessionToken) => {
  if (!sessionToken) return false

  const response = await fetch('https://www.afisha.ru/rests/auth/register/rambler-id/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    credentials: 'include',
    body: new URLSearchParams({ SessionID: sessionToken }),
  })

  switch (response.status) {
    case 200: {
      return true
    }
    case 403: {
      console.error('Error 403: The user is most likely blocked')
      return false
    }
    default: {
      console.error('Unknown error > response ', response)
      return false
    }
  }
}

/**
 * @function authorizationRestsLogout
 * @description
 * @returns true | false
 */
const authorizationRestsLogout = async () => {
  const reqToken = parseCookie('csrftoken')
  if (!reqToken) return false

  try {
    await fetch('https://www.afisha.ru/rests/auth/unregistercurrentsession/', {
      method: 'POST',
      cache: 'no-cache',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        'X-CSRFToken': reqToken,
      },
    })
    return true
  } catch (e) {
    console.error(e)
    return false
  }
}

// * функции для работы с Afisha API *

/**
 * @function authorizationAfishaLogin
 * @description данный логин нужен чтобы корректно работали методы API большой Афиши
 *    (в частности получение куки _afishaidJWT)
 * @description авторизация для большой Афиши должна быть в конце всей цепочки запросов
 * @param rsid - для запроса требуется rsidx из запроса getRsidx
 * @returns true | false
 */
const authorizationAfishaLogin = async (rsid) => {
  const response = await fetch('https://www.afisha.ru/api/auth/login/', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: JSON.stringify({ Rsid: rsid }),
  })

  switch (response.status) {
    case 200: {
      return true
    }
    default: {
      console.error('Error request https://www.afisha.ru/api/auth/login/')
      return false
    }
  }
}

/**
 * @function authorizationAfishaLogout
 * @returns true | false
 */
const authorizationAfishaLogout = async () => {
  const response = await fetch('https://www.afisha.ru/api/auth/logout/', {
    method: 'POST',
    headers: { Accept: 'application/json' },
  })

  switch (response.status) {
    case 200: {
      return true
    }
    default: {
      console.error('Error request https://www.afisha.ru/api/auth/logout/')
      return false
    }
  }
}

// * функции для работы с Rambler ID *

/**
 * @function authorizationRamblerIdOpenAuth
 */
const authorizationRamblerIdOpenAuth = (options?: Record<string, unknown>) => {
  window.ramblerIdHelper?.openAuth({
    rname: 'afisha',
    theme: 'afisha',
    path: '/login-20/phone-login',
    back: 'https://www.afisha.ru/msk/restaurants/',
    ...options,
  })
}

/**
 * @function authorizationRamblerIdCloseAuth
 */
const authorizationRamblerIdCloseAuth = () => {
  window.ramblerIdHelper?.closeAuth()
}

/**
 * @function authorizationRamblerIdLogout
 */
const authorizationRamblerIdLogout = () => {
  window.ramblerIdHelper?.logout()
}

/**
 * @function authorizationRamblerIdGetSessionToken
 */
const authorizationRamblerIdGetSessionToken = (
  callback: (data: { ctime: number; token: string; ttl: number } | null) => void,
) => {
  window.ramblerIdHelper?.getSessionToken(callback)
}

/**
 * @function authorizationRamblerIdOnLogin
 */
const authorizationRamblerIdOnLogin = (callback) => {
  window.ramblerIdHelper?.addListener('login', callback)
}

/**
 * @function authorizationRamblerIdOnOauthLogin
 */
const authorizationRamblerIdOnOauthLogin = (callback) => {
  window.ramblerIdHelper?.addListener('oauthlogin', callback)
}

/**
 * @function authorizationRamblerIdOnLogout
 */
const authorizationRamblerIdOnLogout = (callback) => {
  window.ramblerIdHelper?.addListener('logout', callback)
}

/**
 * @function authorizationRamblerIdRemoveLogout
 */
const authorizationRamblerIdRemoveLogout = (callback) => {
  window.ramblerIdHelper?.removeListener('logout', callback)
}

// экспорт основного объекта с функциями
const Authorization = {
  isInit: () => isInit,
  listeners,
  init,
  _callListeners,
  addListener,
  removeListener,
  openAuth,
  isAuthorized,
  login,
  logout,
}

export default Authorization
